// A work session is a projection of its agent's own stream (ws-unify B2): the entries whose
// rows the daemon stamped with this session. One agent serves several sessions (reuse) and
// sessionless channel turns, so its stream is never one session's.
import { seqRangeOf } from "./plugins/agents/history/rows.js";
import type { StreamEntry } from "./plugins/agents/history/types.js";

// A row's session: a string, null for a row of no session, undefined when not known.
export type RowSessionOf = (seq: number) => string | null | undefined;

// The session an entry proves on its own, or undefined when it only continues its turn.
export function entrySession(
  e: StreamEntry,
  sessionOf: RowSessionOf,
  isLedger: (id: string) => boolean,
): string | null | undefined {
  if (e.kind === "user_message" && e.workSessionId) return e.workSessionId;
  const range = seqRangeOf(e);
  if (range) return sessionOf(range.max) ?? sessionOf(range.min);
  if (e.kind === "turn_boundary")
    return e.afterSeq === undefined ? undefined : sessionOf(e.afterSeq);
  // null: a prompt sent from no session's composer, or a DM ledger reply, belongs to none.
  if (e.kind === "user_message" || isLedger(e.id)) return null;
  return undefined;
}

// Whether the stream already holds the session's first row (`firstSeq`, from the page that
// opened it): paging older past it only reads other sessions. Unknown → never reached.
export function reachedHistoryFloor(
  entries: readonly StreamEntry[],
  firstSeq: number | undefined,
): boolean {
  if (firstSeq === undefined) return false;
  for (const e of entries) {
    const range = seqRangeOf(e);
    if (range) return range.min <= firstSeq;
  }
  return false;
}

// Entries of `sessionId`, in stream order. An entry that proves nothing continues the turn it
// sits in; a divider ends that turn, so nothing after it is claimed until a row says so.
export function projectSessionEntries(
  entries: readonly StreamEntry[],
  sessionId: string,
  sessionOf: RowSessionOf,
  isLedger: (id: string) => boolean = () => false,
): StreamEntry[] {
  const out: StreamEntry[] = [];
  let current: string | null | undefined;
  for (const e of entries) {
    const own = entrySession(e, sessionOf, isLedger);
    if (own !== undefined) current = own;
    if (current === sessionId) out.push(e);
    if (e.kind === "turn_boundary") current = undefined;
  }
  return out;
}
